
import { defineComponent, onBeforeMount, reactive } from 'vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import { ContractRegister, ContractStatusT, DocumentTypeT } from '@/core/types/contracts.types'
import { CompanyT } from '@/core/types/companies.types'
import { contracts } from '@/core/realisations/contracts.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { ContractsRegisterColumns as COLUMNS } from '@/core/constants/columns/Contracts.columns'
import { DataState } from '@/core/classes/DataState.class'
import { useFile } from '@/common/composable/useFile'
import { UserT } from '@/core/types/users.types'
import { users } from '@/core/realisations/users.realisation'
import ContractRegisterAddNew from '@/modules/ContractsModule/components/ContractRegisterAddNew/index.vue'
import useAccount from '@/common/composable/useAccount'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import ContractRegisterAdd from '@/modules/ContractsModule/components/ContractRegisterAdd/index.vue'
import D24Form from '@/modules/ContractsModule/components/D24Form/index.vue'
import AttachDocumentForm from '@/modules/ContractsModule/components/AttachDocumentForm/index.vue'
import useNotifications from '@/common/composable/useNotifications'
import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'
import { formatDateFromBackend, formatDateWithTimeFromBackend } from '@/common/helpers/formatDate'

const INITIATOR_IDS = [14, 20, 43, 34]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        FailedFetch,
        Cheap,
        TheTable,
        TableScrollWrapper,
        ContractRegisterAddNew,
        ButtonDropdown,
        ContractRegisterAdd,
        'd24-form': D24Form,
        AttachDocumentForm,
    },
    setup() {
        const { saveFile } = useFile()
        const { havePermissions } = useAccount()
        const notifications = useNotifications()
        const tablesStore = useTablesStore()

        const state = reactive({
            contracts: new DataState<ContractRegister>({}),
            is_add: false,
        })

        const state_add = reactive({
            is_open: false,
            contract: null as ContractRegister | null,
        })

        const state_d24 = reactive({
            is_open: false,
            contract: null as ContractRegister | null,
        })

        const state_attach = reactive({
            is_open: false,
            is_loading: false,
            contract: null as ContractRegister | null,
        })

        const filters_state = reactive({
            limit: 50,
            //
            number: null as string | null,
            subdivisionIds: [] as number[],
            typeIds: [] as number[],
            dateOfIssueFrom: null as string | null,
            dateOfIssueTo: null as string | null,
            initiatorIds: [] as number[],
            statusIds: [] as number[],
            companyName: null as string | null,
            companyBin: null as string | null,
            dateCreatedFrom: null as string | null,
            dateCreatedTo: null as string | null,
        })

        const data_state = reactive({
            subdivisions: new DataState<any>({}),
            companies: new DataState<CompanyT>({}),
            types: new DataState<DocumentTypeT>({}),
            initiators: new DataState<UserT>({}),
            statuses: new DataState<ContractStatusT>({}),
        })

        const loadTypes = async (page: number) => {
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page })
                data_state.types.list = response.data
                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadMoreTypes = async (page: number) => {
            if (page > data_state.types.pagination.total_pages) return
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page })
                data_state.types.list = data_state.types.list.concat(response.data)

                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadSubdivisions = async () => {
            try {
                data_state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({ page: 1 })
                data_state.subdivisions.list = response.data

                data_state.subdivisions.is_failed = false
                data_state.subdivisions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.subdivisions.failure.catchError(error)

                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_failed = true
                }
            } finally {
                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_loading = false
                }
            }
        }

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true
                const response = await contracts.getStatusesRegister(0)
                data_state.statuses.list = response.data

                data_state.statuses.is_failed = false
                data_state.statuses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.statuses.failure.catchError(error)

                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_failed = true
                }
            } finally {
                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_loading = false
                }
            }
        }

        const loadInitiators = async (page: number, search?: string) => {
            data_state.initiators.pagination.current_page = page

            try {
                data_state.initiators.is_loading = true

                const params = {
                    page,
                    name: search,
                    roleIds: INITIATOR_IDS,
                }
                const response = await users.getList(params)

                data_state.initiators.list = response.data
                data_state.initiators.pagination.total_pages = response.meta.last_page

                data_state.initiators.is_failed = false
                data_state.initiators.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.initiators.failure.catchError(error)

                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_failed = true
                }
            } finally {
                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_loading = false
                }
            }
        }

        const loadMoreInitiators = async (page: number) => {
            if (page > data_state.initiators.pagination.total_pages) return
            data_state.initiators.pagination.current_page = page

            try {
                data_state.initiators.is_loading = true

                const params = {
                    page,
                    roleIds: INITIATOR_IDS,
                }
                const response = await users.getList(params)
                data_state.initiators.list = data_state.initiators.list.concat(response.data)

                data_state.initiators.pagination.total_pages = response.meta.last_page

                data_state.initiators.is_failed = false
                data_state.initiators.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.initiators.failure.catchError(error)

                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_failed = true
                }
            } finally {
                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_loading = false
                }
            }
        }

        const searchInitiators = async (search_query: string) => {
            await loadInitiators(1, search_query)
        }

        const unshiftFindedInitiator = async (inititator: UserT) => {
            await loadInitiators(data_state.initiators.pagination.current_page)
            data_state.initiators.list.unshift(inititator)
        }

        const loadRegisterContracts = async (page: number) => {
            state.contracts.pagination.current_page = page

            const params = {
                page,
                ...filters_state,
            }

            try {
                state.contracts.is_loading = true

                const response = await contracts.getRegisterList(params)

                state.contracts.list = response.data
                state.contracts.pagination.total_pages = response.meta.last_page
                state.contracts.pagination.total_items = response.meta.total

                state.contracts.is_failed = false
                state.contracts.failure.error_state.is_cancel = false
            } catch (error) {
                state.contracts.failure.catchError(error)

                if (!state.contracts.failure.error_state.is_cancel) {
                    state.contracts.is_failed = true
                }
            } finally {
                if (!state.contracts.failure.error_state.is_cancel) {
                    state.contracts.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadRegisterContracts(state.contracts.pagination.current_page)
        }

        const resetFilters = () => {
            filters_state.number = null
            filters_state.subdivisionIds = []
            filters_state.typeIds = []
            filters_state.dateOfIssueFrom = null
            filters_state.dateOfIssueTo = null
            filters_state.initiatorIds = []
            filters_state.statusIds = []
            filters_state.companyName = null
            filters_state.companyBin = null
            filters_state.dateCreatedFrom = null
            filters_state.dateCreatedTo = null

            loadRegisterContracts(1)
        }

        const onContractAdded = () => {
            state.is_add = false
            loadRegisterContracts(state.contracts.pagination.current_page)
        }

        const exportExcel = async () => {
            try {
                state.contracts.is_loading = true

                const response = await contracts.exportExcelRegister(filters_state)
                saveFile(response, 'Реестр договоров')
            } catch (e) {
                console.log(e)
            } finally {
                state.contracts.is_loading = false
            }
        }

        const getStatusColor = (id: number) => {
            if (id === 4) return 'red'
            if ([5, 7, 8].includes(id)) return 'green'
            return 'yellow'
        }

        const toggleAdd = (contract?: ContractRegister) => {
            if (contract) {
                state_add.contract = contract
            } else {
                state_add.contract = null
            }
            state_add.is_open = !state_add.is_open
        }

        const onAdded = () => {
            state_add.is_open = false
            state_add.contract = null

            loadRegisterContracts(1)
        }

        const toggleD24 = (contract?: ContractRegister) => {
            if (contract) {
                state_d24.contract = contract
            } else {
                state_d24.contract = null
            }
            state_d24.is_open = !state_d24.is_open
        }

        const onD24Submit = () => {
            state_d24.is_open = false
            state_d24.contract = null

            loadRegisterContracts(1)
        }

        const toggleAttach = (contract?: ContractRegister) => {
            if (contract) {
                state_attach.contract = contract
            } else {
                state_attach.contract = null
            }
            state_attach.is_open = !state_attach.is_open
        }

        const cancelAttach = () => {
            state_attach.is_open = false
            state_attach.contract = null

            loadRegisterContracts(1)
        }

        const onAttach = async (form_data: FormData) => {
            if (!state_attach.contract) return

            try {
                state_attach.is_loading = true
                await contracts.attachDocument(form_data)
                cancelAttach()
                notifications.notify({ title: 'Допик успешно прикреплен!', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state_attach.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadInitiators(1)
            loadSubdivisions()
            loadStatuses()
            loadTypes(1)
            loadRegisterContracts(1)
        })

        return {
            state_add,
            state_d24,
            state_attach,
            COLUMNS,
            state,
            filters_state,
            data_state,
            tablesStore,
            toggleAttach,
            onAttach,
            toggleAdd,
            onAdded,
            toggleD24,
            onD24Submit,
            loadSubdivisions,
            loadTypes,
            loadMoreTypes,
            loadRegisterContracts,
            resetFilters,
            onContractAdded,
            exportExcel,
            onLimitChange,
            loadInitiators,
            loadMoreInitiators,
            searchInitiators,
            unshiftFindedInitiator,
            loadStatuses,
            getStatusColor,
            havePermissions,
            formatDateFromBackend,
            formatDateWithTimeFromBackend
        }
    },
})
